import { useState } from 'react';

export default function NewsletterCTA() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const body = new URLSearchParams({ email: email.trim() });
      const res = await fetch('https://readdy.ai/api/form/d6v57851l1ubpb129c0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  return (
    <section className="w-full py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl border border-teal-500/25 bg-gradient-to-br from-gray-900 via-gray-900/95 to-gray-900">
          {/* Subtle glow */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(20,184,166,0.10),transparent_65%)] pointer-events-none"></div>

          <div className="relative z-10 px-8 py-9 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/25 mb-5">
              <i className="ri-mail-send-line text-teal-400 text-xs"></i>
              <span className="text-teal-400 text-xs font-semibold tracking-wide uppercase">Newsletter</span>
            </div>

            <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">Stay Ahead of AI</h2>
            <p className="text-gray-400 text-sm mb-7 max-w-sm mx-auto leading-relaxed">
              Get weekly AI tool picks delivered free to your inbox.
            </p>

            {status === 'success' ? (
              <div className="flex items-center justify-center gap-2 py-3 px-6 bg-teal-500/15 border border-teal-500/30 rounded-xl">
                <i className="ri-checkbox-circle-fill text-teal-400 text-lg"></i>
                <span className="text-teal-300 font-semibold text-sm">You&apos;re subscribed. Welcome aboard!</span>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={status === 'loading'}
                  className="flex-1 w-full px-4 py-3 rounded-lg bg-gray-800/80 border border-gray-700/70 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-teal-500/60 focus:ring-1 focus:ring-teal-500/30 transition-colors disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={status === 'loading' || !email.trim()}
                  className="whitespace-nowrap w-full sm:w-auto px-6 py-3 bg-teal-500 hover:bg-teal-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                      Subscribing...
                    </span>
                  ) : (
                    'Subscribe Free'
                  )}
                </button>
              </form>
            )}

            {status === 'error' && (
              <p className="mt-3 text-rose-400 text-xs">{errorMsg}</p>
            )}

            <p className="mt-4 text-gray-600 text-xs">No spam. Unsubscribe anytime.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
